"use client";
import React, { useState, useEffect, useRef } from "react";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { NavbarDocument } from "../../../prismicio-types";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react";
import ActionButton from "../shared-components/ActionButton";
import clsx from "clsx";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { isFilled } from "@prismicio/client";

export default function NavBar({ navbar }: { navbar: NavbarDocument<string> }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [campusDropdownOpen, setCampusDropdownOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "auto";
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prevState) => !prevState);
    if (mobileMenuOpen) {
      setCampusDropdownOpen(false);
      setServicesDropdownOpen(false);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setCampusDropdownOpen(false);
    setServicesDropdownOpen(false);
  };

  return (
    <div
      className={`fixed left-0 w-full px-2 lg:px-0 z-50 transition-all duration-100 ease-in-out py-5 ${
        isScrolled ? "bg-white/35 backdrop-blur-3xl top-0" : "bg-white top-0"
      }`}
    >
      <nav className="mx-auto px-6 md:px-[30px] lg:px-[60px] xl:px-20 w-full flex items-center justify-between gap-[21px] container">
        {/* Logo */}
        <div className="shrink-0">
          <Link href={"/"}>
            <PrismicNextImage
              className="h-10 lg:h-[60px] w-auto"
              alt=""
              field={navbar.data.logo}
              aria-label="logo"
            />
          </Link>
        </div>

        {/* Desktop nav */}
        <NavigationMenu
          viewport={false}
          className="hidden lg:flex items-center font-light gap-[42px] cursor-pointer px-7 py-5 bg-[#EBF7FF] rounded-full"
        >
          <NavigationMenuList className="flex items-center gap-8">
            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={`font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 bg-transparent! h-fit w-fit p-0 ${
                  "url" in navbar.data.home_link && navbar.data.home_link.url
                    ? pathname === navbar.data.home_link.url
                    : false
                      ? "text-secondary rounded-full"
                      : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                }`}
              >
                <PrismicNextLink
                  field={navbar.data.home_link}
                  className={
                    (
                      "url" in navbar.data.home_link &&
                      navbar.data.home_link.url
                        ? pathname === navbar.data.home_link.url
                        : false
                    )
                      ? "text-secondary rounded-full hover:text-secondary focus:text-secondary"
                      : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                  }
                >
                  {navbar.data.home_label}
                </PrismicNextLink>
              </NavigationMenuLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={`font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 bg-transparent! h-fit w-fit p-0 ${
                  "url" in navbar.data.about_us_link &&
                  navbar.data.about_us_link.url
                    ? pathname === navbar.data.about_us_link.url
                    : false
                      ? "text-secondary rounded-full"
                      : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                }`}
              >
                <PrismicNextLink
                  field={navbar.data.about_us_link}
                  className={
                    (
                      "url" in navbar.data.about_us_link &&
                      navbar.data.about_us_link.url
                        ? pathname === navbar.data.about_us_link.url
                        : false
                    )
                      ? "text-secondary rounded-full hover:text-secondary focus:text-secondary"
                      : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                  }
                >
                  {navbar.data.about_us_label}
                </PrismicNextLink>
              </NavigationMenuLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              {isFilled.group(navbar.data.campus_and_facilities_children) ? (
                <>
                  <NavigationMenuTrigger className="bg-transparent! mb-1 font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none h-fit w-fit p-0">
                    {navbar.data.campus_and_facilities_label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-2 bg-white">
                      {navbar.data.campus_and_facilities_children.map(
                        (item) => (
                          <li key={item.title}>
                            <NavigationMenuLink
                              asChild
                              className={`font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 bg-transparent! ${
                                "url" in item.link && item.link.url
                                  ? pathname === item.link.url
                                  : false
                                    ? "text-secondary rounded-full"
                                    : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                              }`}
                            >
                              <PrismicNextLink
                                field={item.link}
                                className={
                                  (
                                    "url" in item.link && item.link.url
                                      ? pathname === item.link.url
                                      : false
                                  )
                                    ? "text-secondary rounded-full hover:text-secondary focus:text-secondary"
                                    : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                                }
                              >
                                {item.title}
                              </PrismicNextLink>
                            </NavigationMenuLink>
                          </li>
                        )
                      )}
                    </ul>
                  </NavigationMenuContent>
                </>
              ) : (
                <NavigationMenuLink
                  asChild
                  className={`font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 bg-transparent! h-fit w-fit p-0 ${
                    "url" in navbar.data.campus_and_facilities_link &&
                    navbar.data.campus_and_facilities_link.url
                      ? pathname === navbar.data.campus_and_facilities_link.url
                      : false
                        ? "text-secondary rounded-full"
                        : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                  }`}
                >
                  <PrismicNextLink
                    field={navbar.data.campus_and_facilities_link}
                    className={
                      (
                        "url" in navbar.data.campus_and_facilities_link &&
                        navbar.data.campus_and_facilities_link.url
                          ? pathname ===
                            navbar.data.campus_and_facilities_link.url
                          : false
                      )
                        ? "text-secondary rounded-full hover:text-secondary focus:text-secondary"
                        : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                    }
                  >
                    {navbar.data.campus_and_facilities_label}
                  </PrismicNextLink>
                </NavigationMenuLink>
              )}
            </NavigationMenuItem>
            <NavigationMenuItem>
              {isFilled.group(navbar.data.student_services_children) ? (
                <>
                  <NavigationMenuTrigger className="bg-transparent! mb-1 font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none h-fit! w-fit! p-0!">
                    {navbar.data.student_services_label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-2 bg-white">
                      {navbar.data.student_services_children.map((item) => (
                        <li key={item.title}>
                          <NavigationMenuLink
                            asChild
                            className={`font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 bg-transparent! ${
                              "url" in item.link && item.link.url
                                ? pathname === item.link.url
                                : false
                                  ? "text-secondary rounded-full"
                                  : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                            }`}
                          >
                            <PrismicNextLink
                              field={item.link}
                              className={
                                (
                                  "url" in item.link && item.link.url
                                    ? pathname === item.link.url
                                    : false
                                )
                                  ? "text-secondary rounded-full hover:text-secondary focus:text-secondary"
                                  : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                              }
                            >
                              {item.title}
                            </PrismicNextLink>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </>
              ) : (
                <NavigationMenuLink
                  asChild
                  className={`font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 bg-transparent! h-fit w-fit p-0 ${
                    "url" in navbar.data.student_services_link &&
                    navbar.data.student_services_link.url
                      ? pathname === navbar.data.student_services_link.url
                      : false
                        ? "text-secondary rounded-full"
                        : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                  }`}
                >
                  <PrismicNextLink
                    field={navbar.data.student_services_link}
                    className={
                      (
                        "url" in navbar.data.student_services_link &&
                        navbar.data.student_services_link.url
                          ? pathname === navbar.data.student_services_link.url
                          : false
                      )
                        ? "text-secondary rounded-full hover:text-secondary focus:text-secondary"
                        : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                    }
                  >
                    {navbar.data.student_services_label}
                  </PrismicNextLink>
                </NavigationMenuLink>
              )}
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={`font-medium text-sm font-archivo leading-5 cursor-pointer transition-colors duration-200 bg-transparent! h-fit w-fit p-0 ${
                  "url" in navbar.data.contact_link &&
                  navbar.data.contact_link.url
                    ? pathname === navbar.data.contact_link.url
                    : false
                      ? "text-secondary rounded-full"
                      : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                }`}
              >
                <PrismicNextLink
                  field={navbar.data.contact_link}
                  className={
                    (
                      "url" in navbar.data.contact_link &&
                      navbar.data.contact_link.url
                        ? pathname === navbar.data.contact_link.url
                        : false
                    )
                      ? "text-secondary rounded-full hover:text-secondary focus:text-secondary"
                      : "hover:text-secondary focus:text-secondary text-primary focus:text-light-blue focus:outline-none"
                  }
                >
                  {navbar.data.contact_label}
                </PrismicNextLink>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Action button */}
        <div className="flex items-center gap-10">
          <div className="hidden md:block">
            <PrismicNextLink field={navbar.data.cta_url}>
              <ActionButton btnTxt={navbar.data.cta_url.text ?? ""} />
            </PrismicNextLink>
          </div>
          <button
            className="p-2 bg-[#EBF7FF] rounded-[10px] mobile-menu-button lg:hidden flex flex-col justify-center items-center gap-1.5"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <Menu />
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <div
        ref={mobileMenuRef}
        className={`fixed inset-0 bg-[#EBF7FF] z-9999 min-h-screen lg:hidden transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-8 border-b-2 border-gray-100/20 pb-5">
            <Link href={"/"} onClick={closeMobileMenu} aria-label="logo">
              <PrismicNextImage
                className="h-11 w-auto"
                alt=""
                field={navbar.data.logo}
              />
            </Link>
            <div className="flex items-center gap-4">
              <PrismicNextLink field={navbar.data.cta_url} onClick={closeMobileMenu}>
                <ActionButton btnTxt="Apply Now" />
              </PrismicNextLink>

              <button
                onClick={closeMobileMenu}
                aria-label="Close menu"
                className="p-2 text-primary rounded-[10px] bg-[white]"
              >
                <X />
              </button>
            </div>
          </div>

          <nav className="flex flex-col space-y-4">
            <div className="space-y-3 pt-3 text-center">
              {/* Home Link */}
              <PrismicNextLink
                field={navbar.data.home_link}
                onClick={closeMobileMenu}
                className={clsx(
                  `block text-primary text-xl py-3 transition-colors duration-200`,
                  "url" in navbar.data.home_link &&
                    navbar.data.home_link.url &&
                    pathname === navbar.data.home_link.url
                    ? "bg-white border border-white rounded-md px-3"
                    : ""
                )}
              >
                {navbar.data.home_label}
              </PrismicNextLink>

              {/* About Us Link */}
              <PrismicNextLink
                field={navbar.data.about_us_link}
                onClick={closeMobileMenu}
                className={clsx(
                  `block text-primary text-xl py-3 transition-colors duration-200`,
                  "url" in navbar.data.about_us_link &&
                    navbar.data.about_us_link.url &&
                    pathname === navbar.data.about_us_link.url
                    ? "bg-white border border-white rounded-md px-3"
                    : ""
                )}
              >
                {navbar.data.about_us_label}
              </PrismicNextLink>

              {/* Campus and Facilities - with dropdown if children exist */}
              {isFilled.group(navbar.data.campus_and_facilities_children) ? (
                <div className="w-full">
                  <button
                    onClick={() => setCampusDropdownOpen(!campusDropdownOpen)}
                    className="w-full flex items-center justify-center gap-2 text-primary text-xl py-3 transition-colors duration-200"
                  >
                    {navbar.data.campus_and_facilities_label}
                    {campusDropdownOpen ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  {campusDropdownOpen && (
                    <div className="mt-2 space-y-2 rounded-md py-2 px-4">
                      {navbar.data.campus_and_facilities_children.map(
                        (item) => (
                          <PrismicNextLink
                            key={item.title}
                            field={item.link}
                            onClick={closeMobileMenu}
                            className={clsx(
                              `block text-primary text-base py-2 transition-colors duration-200`,
                              "url" in item.link &&
                                item.link.url &&
                                pathname === item.link.url
                                ? "text-secondary font-semibold"
                                : ""
                            )}
                          >
                            {item.title}
                          </PrismicNextLink>
                        )
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <PrismicNextLink
                  field={navbar.data.campus_and_facilities_link}
                  onClick={closeMobileMenu}
                  className={clsx(
                    `block text-primary text-xl py-3 transition-colors duration-200`,
                    "url" in navbar.data.campus_and_facilities_link &&
                      navbar.data.campus_and_facilities_link.url &&
                      pathname === navbar.data.campus_and_facilities_link.url
                      ? "bg-white border border-white rounded-md px-3"
                      : ""
                  )}
                >
                  {navbar.data.campus_and_facilities_label}
                </PrismicNextLink>
              )}

              {/* Student Services - with dropdown if children exist */}
              {isFilled.group(navbar.data.student_services_children) ? (
                <div className="w-full">
                  <button
                    onClick={() =>
                      setServicesDropdownOpen(!servicesDropdownOpen)
                    }
                    className="w-full flex items-center justify-center gap-2 text-primary text-xl py-3 transition-colors duration-200"
                  >
                    {navbar.data.student_services_label}
                    {servicesDropdownOpen ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  {servicesDropdownOpen && (
                    <div className="mt-2 space-y-2 rounded-md py-2 px-4">
                      {navbar.data.student_services_children.map((item) => (
                        <PrismicNextLink
                          key={item.title}
                          field={item.link}
                          onClick={closeMobileMenu}
                          className={clsx(
                            `block text-primary text-base py-2 transition-colors duration-200`,
                            "url" in item.link &&
                              item.link.url &&
                              pathname === item.link.url
                              ? "text-secondary font-semibold"
                              : ""
                          )}
                        >
                          {item.title}
                        </PrismicNextLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <PrismicNextLink
                  field={navbar.data.student_services_link}
                  onClick={closeMobileMenu}
                  className={clsx(
                    `block text-primary text-xl py-3 transition-colors duration-200`,
                    "url" in navbar.data.student_services_link &&
                      navbar.data.student_services_link.url &&
                      pathname === navbar.data.student_services_link.url
                      ? "bg-white border border-white rounded-md px-3"
                      : ""
                  )}
                >
                  {navbar.data.student_services_label}
                </PrismicNextLink>
              )}

              {/* Contact Link */}
              <PrismicNextLink
                field={navbar.data.contact_link}
                onClick={closeMobileMenu}
                className={clsx(
                  `block text-primary text-xl py-3 transition-colors duration-200`,
                  "url" in navbar.data.contact_link &&
                    navbar.data.contact_link.url &&
                    pathname === navbar.data.contact_link.url
                    ? "bg-white border border-white rounded-md px-3"
                    : ""
                )}
              >
                {navbar.data.contact_label}
              </PrismicNextLink>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}
